/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { camelCase } from 'lodash';
import cn from 'classnames';

import type { TFieldProps } from 'client/components/common/BuildingBlocks/Fields/types';
import { useClientStoryDictionary } from 'common/i18n/hooks/useClientStoryDictionary';
import css from './AnswerInput.scss';
import { ANSWER_INPUT_LETTERS_DATA_ATTR, ANSWER_INPUT_WORDS_DATA_ATTR } from './constants';

export interface Props extends TFieldProps {
	correctValue: string;
	textAlign: 'left' | 'center' | 'right';
	inputRef: React.MutableRefObject<HTMLInputElement | null>;
}

export const MASK_CHAR = '*';

export const LetterModeInput = (props: Props) => {
	const { t } = useClientStoryDictionary();
	const { correctValue, textAlign, inputRef, ...inputProps } = props;
	const noValue = t('ANSWER_INPUT.noValue');
	const words = (correctValue || noValue).split(',')[0].trim().split(' ');
	const letterRefs = React.useRef<(HTMLInputElement | null)[]>([]);
	const placeholderLetters = getPlaceholderLetters(words.join(' '), correctValue ? props.placeholder ?? '' : noValue);
	let letterIndex = 0;

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const index = Number(e.target.dataset.index);
		const mask = correctValue.trim().replace(/[^\s]/g, MASK_CHAR).split('');
		const splitedValue = props.value?.toString().split('') ?? [];

		const nextValue = mask
			.reduce((acc, current, i) => {
				acc[i] = i === index ? value || MASK_CHAR : splitedValue[i] || current;
				return acc;
			}, [] as string[])
			.join('');

		if (props.onChange) {
			const event = {
				...e,
				target: {
					...e.target,
					value: nextValue,
				},
			};

			props.onChange(event);

			let focusIndex = index + 1;
			while (focusIndex < correctValue.length && correctValue[focusIndex] === ' ') {
				focusIndex += 1;
			}
			if (value && letterRefs.current[focusIndex]) {
				letterRefs.current[focusIndex]?.focus();
			}
		}
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const index = Number(e.currentTarget.dataset.index);

		if (e.key === 'Backspace' && !e.currentTarget.value) {
			e.preventDefault();

			let focusIndex = index - 1;
			while (focusIndex >= 0 && correctValue[focusIndex] === ' ') {
				focusIndex -= 1;
			}
			if (letterRefs.current[focusIndex]) {
				if (letterRefs.current[focusIndex]) {
					// Erase the previous letter
					const prevInput = letterRefs.current[focusIndex];
					if (prevInput) {
						const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
							window.HTMLInputElement.prototype,
							'value'
						)?.set;
						nativeInputValueSetter?.call(prevInput, '');
						const event = new Event('input', { bubbles: true });
						prevInput.dispatchEvent(event);
						prevInput.focus();
					}
				}
			}
		}

		props.onKeyDown?.(e);
	};

	return (
		<div
			data-selector={ANSWER_INPUT_WORDS_DATA_ATTR}
			className={cn(css.words, css[camelCase(`text-align-${textAlign}`)], !correctValue && css.noValue)}
		>
			{words.map((word, wordIndex) => {
				const letters = word.split('');
				const wordResult = (
					<div
						data-selector={ANSWER_INPUT_LETTERS_DATA_ATTR}
						className={css.word}
						key={`${word}-${wordIndex}`}
					>
						{letters.map(letter => {
							const i = letterIndex;
							const letterValue = props.value ? props.value.toString().charAt(letterIndex) : '';
							const placeholder = !props.value ? placeholderLetters.splice(0, 1)[0] : '';
							const letterResult = (
								<input
									data-index={letterIndex}
									{...inputProps}
									value={letterValue !== MASK_CHAR ? letterValue : ''}
									maxLength={1}
									placeholder={placeholder}
									key={`${letter}-${letterIndex}`}
									onChange={onChange}
									onKeyDown={onKeyDown}
									ref={el => {
										letterRefs.current[i] = el;

										if (i === 0) {
											inputRef.current = el;
										}
									}}
								/>
							);

							letterIndex += 1;
							return letterResult;
						})}
					</div>
				);

				letterIndex += 1;
				return wordResult;
			})}
		</div>
	);
};

function getPlaceholderLetters(correctValue: string, placeholder: string) {
	const result: string[] = [];

	placeholder.split('').forEach((p, i) => {
		const v = correctValue[i];
		const vPrev = correctValue[i - 1];
		const vNext = correctValue[i + 1];

		if (p !== ' ' || (v !== ' ' && !(vPrev === ' ' && vNext === ' '))) {
			result.push(p);
		}
	});

	return result;
}
